/**
 * 実装の解決を待ってから委譲するサービス。
 *
 * Firebase 実装は動的 import(= チャンク取得)のあとにしか作れないが、
 * ストアは起動時に同期でサービスを受け取る必要がある。
 * `ProgressService` / `GachaService` のメソッドは全て非同期なので、
 * 「Promise を受け取って毎回 await するだけ」の薄い委譲で埋められる。
 */
import type { AccountUser, AuthOutcome, AuthService } from './auth';
import type {
  ChallengeProfile,
  ChallengeReceipt,
  ChallengeService,
  ChallengeSubmission,
  ChallengeView,
  LeaderboardView,
} from './challenge';
import type { GachaPullInput, GachaPullResult, GachaService } from './gacha';
import type { Difficulty } from '@paltyping/engine';
import type { LeagueService, LeagueView } from './league';
import type {
  ProgressService,
  ProgressSnapshot,
  StageResultReceipt,
  StageResultSubmission,
} from './progress';
import type {
  PuzzleRewardReceipt,
  PuzzleRewardService,
  PuzzleSessionSubmission,
} from './puzzleRewards';
import type { QuizRewardReceipt, QuizRewardService, QuizSessionSubmission } from './quizRewards';
import type { TutorContext, TutorHintInput, TutorKind, TutorReply, TutorService } from './tutor';
import type { TransferCode, TransferResult, TransferService } from './transfer';

export class LazyProgressService implements ProgressService {
  constructor(private readonly impl: Promise<ProgressService>) {}

  /** 解決後の実装(テスト・デバッグ用)。 */
  async resolved(): Promise<ProgressService> {
    return this.impl;
  }

  async load(): Promise<ProgressSnapshot> {
    return (await this.impl).load();
  }

  async submitStageResult(input: StageResultSubmission): Promise<StageResultReceipt> {
    return (await this.impl).submitStageResult(input);
  }

  async unlockStage(stageId: string, difficulty: Difficulty): Promise<ProgressSnapshot> {
    const impl = await this.impl;
    const unlock = impl.unlockStage;
    if (!unlock) throw new Error('この進捗サービスはロック解除に対応していません');
    return unlock.call(impl, stageId, difficulty);
  }

  async refreshStageAccess(stageId: string, difficulty: Difficulty): Promise<ProgressSnapshot> {
    const impl = await this.impl;
    const refresh = impl.refreshStageAccess;
    if (!refresh) throw new Error('この進捗サービスはステージ状態照会に対応していません');
    return refresh.call(impl, stageId, difficulty);
  }

  async reset(): Promise<ProgressSnapshot> {
    return (await this.impl).reset();
  }
}

export class LazyGachaService implements GachaService {
  constructor(private readonly impl: Promise<GachaService>) {}

  /** 解決後の実装(テスト・デバッグ用)。 */
  async resolved(): Promise<GachaService> {
    return this.impl;
  }

  async pull(input: GachaPullInput): Promise<GachaPullResult> {
    return (await this.impl).pull(input);
  }

  async balances(): Promise<Readonly<Record<string, number>> | null> {
    return (await this.impl).balances();
  }
}

/**
 * 認証だけは同期の `current()` / `subscribe()` を持つので、
 * 解決前は「まだ誰も居ない(null)」を返し、解決後に購読を引き継ぐ。
 */
export class LazyAuthService implements AuthService {
  readonly supported = true;

  private readonly listeners = new Set<(user: AccountUser | null) => void>();
  private user: AccountUser | null = null;

  constructor(private readonly impl: Promise<AuthService>) {
    // アプリと同じ寿命のシングルトンなので、実装側の購読は張りっぱなしにする
    // (解除するのは画面側のリスナーだけ)。
    void impl.then((auth) => {
      auth.subscribe((user) => {
        this.user = user;
        for (const listener of this.listeners) listener(user);
      });
    });
  }

  /** 解決後の実装(テスト・デバッグ用)。 */
  async resolved(): Promise<AuthService> {
    return this.impl;
  }

  current(): AccountUser | null {
    return this.user;
  }

  subscribe(listener: (user: AccountUser | null) => void): () => void {
    this.listeners.add(listener);
    listener(this.user);
    return () => void this.listeners.delete(listener);
  }

  async completeRedirect(): Promise<AccountUser | null> {
    return (await this.impl).completeRedirect();
  }

  async registerWithEmail(email: string, password: string): Promise<AuthOutcome> {
    return (await this.impl).registerWithEmail(email, password);
  }

  async signInWithEmail(email: string, password: string): Promise<AuthOutcome> {
    return (await this.impl).signInWithEmail(email, password);
  }

  async signInWithGoogle(): Promise<AuthOutcome> {
    return (await this.impl).signInWithGoogle();
  }

  async continueWithExistingAccount(): Promise<AuthOutcome> {
    return (await this.impl).continueWithExistingAccount();
  }

  discardExistingAccount(): void {
    void this.impl.then((auth) => auth.discardExistingAccount());
  }

  async signOut(): Promise<void> {
    return (await this.impl).signOut();
  }
}

/** パズルのコイン報酬(docs/11 §3)。実装が解決するまで待ってから委譲する。 */
export class LazyPuzzleRewardService implements PuzzleRewardService {
  constructor(private readonly impl: Promise<PuzzleRewardService>) {}

  /** 解決後の実装(テスト・デバッグ用)。 */
  async resolved(): Promise<PuzzleRewardService> {
    return this.impl;
  }

  async submitSession(input: PuzzleSessionSubmission): Promise<PuzzleRewardReceipt | null> {
    return (await this.impl).submitSession(input);
  }

  async balance(): Promise<number | null> {
    return (await this.impl).balance();
  }
}

/** まなびの教科コイン報酬(docs/10 §4.3)。実装が解決するまで待ってから委譲する。 */
export class LazyQuizRewardService implements QuizRewardService {
  constructor(private readonly impl: Promise<QuizRewardService>) {}

  /** 解決後の実装(テスト・デバッグ用)。 */
  async resolved(): Promise<QuizRewardService> {
    return this.impl;
  }

  async submitUnit(input: QuizSessionSubmission): Promise<QuizRewardReceipt | null> {
    return (await this.impl).submitUnit(input);
  }

  async balance(band: string, subject: string): Promise<number | null> {
    return (await this.impl).balance(band, subject);
  }
}

/**
 * 週替わりチャレンジ(docs/12 §1)。
 *
 * `supported` だけは**同期**で要る(選択画面がカードを出すか決めるため)。
 * Firebase モードだと分かっている時点で作られるので、ここは常に `true`
 * (SDK の読み込みに失敗したら `createLocalFallbackRuntime` が
 *  `NoChallengeService` に差し替わり、`load()` が `null` を返してカードが消える)。
 */
export class LazyChallengeService implements ChallengeService {
  readonly supported = true;

  constructor(private readonly impl: Promise<ChallengeService>) {}

  /** 解決後の実装(テスト・デバッグ用)。 */
  async resolved(): Promise<ChallengeService> {
    return this.impl;
  }

  async load(): Promise<ChallengeView | null> {
    return (await this.impl).load();
  }

  async submit(input: ChallengeSubmission): Promise<ChallengeReceipt | null> {
    return (await this.impl).submit(input);
  }

  async leaderboard(weekId: string, band: 'all' | number): Promise<LeaderboardView | null> {
    return (await this.impl).leaderboard(weekId, band);
  }

  async saveProfile(input: Partial<ChallengeProfile>): Promise<ChallengeProfile | null> {
    return (await this.impl).saveProfile(input);
  }
}

/**
 * リーグ(docs/12 §2)。`LazyChallengeService` と同じ理由で `supported` は同期。
 *
 * SDK の読み込みに失敗したら `createLocalFallbackRuntime` が `NoLeagueService` に
 * 差し替わり、`load()` が `null` を返してカードが消える。
 */
export class LazyLeagueService implements LeagueService {
  readonly supported = true;

  constructor(private readonly impl: Promise<LeagueService>) {}

  /** 解決後の実装(テスト・デバッグ用)。 */
  async resolved(): Promise<LeagueService> {
    return this.impl;
  }

  async load(): Promise<LeagueView | null> {
    return (await this.impl).load();
  }
}

/**
 * AIチューター(docs/07_learning_support.md §7)。
 *
 * `supported` は同期で読まれる(画面がカードを出すか決める)ので、
 * Firebase モードでは**解決を待たずに `true`** を返す。実装が来る前に押されても
 * `ask()` の中で待つだけ(取れなければ `null` = 画面は静かに何も出さない)。
 */
export class LazyTutorService implements TutorService {
  readonly supported = true;

  constructor(private readonly impl: Promise<TutorService>) {}

  /** 解決後の実装(テスト・デバッグ用)。 */
  async resolved(): Promise<TutorService> {
    return this.impl;
  }

  async ask(kind: TutorKind, context?: TutorContext): Promise<TutorReply | null> {
    const impl = await this.impl;
    if (!impl.supported) return null;
    return impl.ask(kind, context);
  }

  async hint(input: TutorHintInput): Promise<TutorReply | null> {
    const impl = await this.impl;
    if (!impl.supported) return null;
    return impl.hint(input);
  }
}

/**
 * ひきつぎコード(docs/10 §3.4)。`LazyChallengeService` と同じ理由で `supported` は同期
 * (「とうろく」画面がカードを出すか決める)。
 *
 * SDK の読み込みに失敗したら `createLocalFallbackRuntime` が `NoTransferService` に
 * 差し替わり、`issue()` / `redeem()` が `unsupported` で失敗する。
 */
export class LazyTransferService implements TransferService {
  readonly supported = true;

  constructor(private readonly impl: Promise<TransferService>) {}

  /** 解決後の実装(テスト・デバッグ用)。 */
  async resolved(): Promise<TransferService> {
    return this.impl;
  }

  async issue(): Promise<TransferCode> {
    return (await this.impl).issue();
  }

  async redeem(code: string): Promise<TransferResult> {
    return (await this.impl).redeem(code);
  }
}
